import React, { useState } from 'react';
import { FolderPlus, X, BookOpen, Target, AlertCircle } from 'lucide-react';

// Subject option passed from parent (subscribed subjects)
export type SubjectOption = {
  subject_id: string;
  name: string;
};

// Simple form input type for the modal (not the API type)
type ProjectFormInput = {
  title?: string;  // Optional - some students need help figuring out a title
  subject_id: string; // Required - must be a subscribed subject
  goal?: string;
};

type NewProjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: ProjectFormInput) => void;
  subscribedSubjects: SubjectOption[];
};

export const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  subscribedSubjects = [],
}) => {
  const [form, setForm] = useState<ProjectFormInput>({
    title: '',
    subject_id: '',
    goal: '',
  });
  const [subjectError, setSubjectError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate subject is selected
    if (!form.subject_id) {
      setSubjectError('Please select a subject for your project.');
      return;
    }
    setSubjectError(null);
    onCreate({
      title: form.title?.trim() || undefined,
      subject_id: form.subject_id,
      goal: form.goal?.trim() || undefined,
    });
    onClose();
    setForm({ title: '', subject_id: '', goal: '' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative p-6 border-b border-white/10">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full opacity-20 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                <FolderPlus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">New Project</h2>
                <p className="text-xs text-slate-400">Create a project workspace for coaching & planning</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Subject (required) */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              Subject <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              <select
                required
                value={form.subject_id}
                onChange={(e) => {
                  setForm((p) => ({ ...p, subject_id: e.target.value }));
                  if (e.target.value) setSubjectError(null);
                }}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white focus:outline-none transition-colors appearance-none ${
                  subjectError ? 'border-red-500/60 focus:border-red-500/80' : 'border-white/10 focus:border-cyan-500/50'
                }`}
              >
                <option value="" className="bg-slate-800 text-slate-400">Select a subject...</option>
                {subscribedSubjects.map((s) => (
                  <option key={s.subject_id} value={s.subject_id} className="bg-slate-800 text-white">
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {subjectError && (
              <div className="flex items-center gap-1.5 mt-1.5 text-red-400 text-xs">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{subjectError}</span>
              </div>
            )}
            {subscribedSubjects.length === 0 && (
              <p className="mt-1.5 text-xs text-yellow-400/80">
                No subscribed subjects found. Subscribe to a subject first.
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Project title (optional)</label>
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="e.g., Water pollution investigation (or leave blank for help choosing)"
                value={form.title || ''}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
              />
            </div>
          </div>

          {/* Goal */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Project goal (optional)</label>
            <textarea
              placeholder="What do you want to achieve? (1–2 sentences)"
              value={form.goal || ''}
              onChange={(e) => setForm((p) => ({ ...p, goal: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={subscribedSubjects.length === 0}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Create project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
